/**
 * The Matches screen's copy and every decision in it (`MEM-024`…`MEM-027`, `MEM-013`).
 *
 * **There is no DOM renderer in this repository and `apps/web`'s test script matches `.test.ts`
 * only**, so a rule inside a component body is a rule with no test. Everything on this screen that
 * could be *wrong* — which heading a past event gets, what the meet-list toggle is counting,
 * which attendees a query keeps — is a pure function here rather than an expression in JSX.
 *
 * Nothing in this file imports the `@/` alias: `tsx --test` resolves relative specifiers and not
 * Vite's, so a pure module that reached for `@/` would be one the test runner could not load.
 */

import { matchesIntroSentence } from '../attendee-preview/constants';

// ─── the match grid (§2.4, item 4) ─────────────────────────────────────────────────────────────

/** The eyebrow over the narrative on a match card — `Web App.dc.html` §3.8, verbatim. */
export const WHY_YOU_SHOULD_MEET_LABEL = 'Why you should meet';

/**
 * **The heading and the sentence under it**, which the prototype changes for a past event
 * (`matchHeading` / `matchSubhead`, lines 2379–2380).
 *
 * *"Your matches"* becomes *"People we suggested you meet"*, and the sentence becomes a sentence in
 * the past tense. That is not decoration: a delivered set is a snapshot (`MEM-026` AC-1), so after
 * the evening it is a record of who was suggested rather than a plan for the night, and a heading
 * in the present tense would be the page pretending the room is still there.
 *
 * The live sentence is `matchesIntroSentence`, which carries the **real** count — the prototype
 * hard-codes *3* on every event (`handoff-corrections.md`).
 */
export const MATCHES_HEADING = 'Your matches';
export const MATCHES_PAST_HEADING = 'People we suggested you meet';
export const MATCHES_PAST_SUBHEAD =
  'These were your suggested matches for this event. Follow up with anyone you met.';

export function matchesHeading(hasEnded: boolean): string {
  return hasEnded ? MATCHES_PAST_HEADING : MATCHES_HEADING;
}

/**
 * **An empty set says it once.** With no suggestions the sentence read *"We picked 0 people we
 * think you should meet…"* directly above the empty box's own *"No matches yet"* — the same fact
 * twice, the first time in awkward English, and a count of nothing described as a choice somebody
 * made. Reported by Brad on 2026-09-20 (TW 44139184). So a live event with nothing to list drops
 * the sentence and lets the box below carry the message; the sentence returns with the first
 * suggestion. A past event keeps its own sentence, which is a statement about the evening rather
 * than about a list, and reads true at any count.
 */
export function matchesSubhead(input: {
  readonly hasEnded: boolean;
  readonly count: number;
}): string | null {
  if (input.hasEnded) return MATCHES_PAST_SUBHEAD;
  return input.count === 0 ? null : matchesIntroSentence(input.count);
}

// ─── the meet-list toggle (§2.4, item 4) ───────────────────────────────────────────────────────

/**
 * The toggle at the right of the heading row, and `MEM-013`'s list seen from this screen.
 *
 * It filters **the attendee grid only** — the prototype's `meetOnly` touches `attendeesFiltered`,
 * `attendeeHeading`, `attendeeSubhead` and the private row, and never the matches above. That is
 * the right split: the matches are a fixed set somebody else chose, and the meet list is the
 * member's own selection out of the room.
 */
export const MEET_ONLY_LABEL = 'People I want to meet';

/** The toggle carries a digit; this is its accessible name (`meetListCountLabel`'s treatment). */
export function meetOnlyControlName(input: {
  readonly count: number;
  readonly on: boolean;
}): string {
  const people = input.count === 1 ? '1 person' : `${String(input.count)} people`;
  return input.on
    ? `Showing only the ${people} on your meet list`
    : `Show only the ${people} on your meet list`;
}

// ─── everyone attending (§2.4, item 4) ─────────────────────────────────────────────────────────

export const ATTENDEES_HEADING = 'Everyone attending';
export const ATTENDEES_SUBHEAD =
  'Beyond your matches — save anyone to your meet list or send a connection request.';
export const ATTENDEES_MEET_ONLY_SUBHEAD = "Everyone you've saved to meet at this event.";
export const ATTENDEES_SEARCH_PLACEHOLDER = 'Search attendees…';
export const ATTENDEES_SEARCH_LABEL = 'Search attendees';
/** The `x` inside the pill, named — an icon-only control with no name is called "button". */
export const ATTENDEES_SEARCH_CLEAR_LABEL = 'Clear the attendee search';

export function attendeesHeading(meetOnly: boolean): string {
  return meetOnly ? MEET_ONLY_LABEL : ATTENDEES_HEADING;
}

export function attendeesSubhead(meetOnly: boolean): string {
  return meetOnly ? ATTENDEES_MEET_ONLY_SUBHEAD : ATTENDEES_SUBHEAD;
}

/** The id the meet-list toggle scrolls to, and the prototype's own `whos-coming` anchor. */
export const ATTENDEES_REGION_ID = 'whos-coming';

/**
 * **Which attendees the grid draws**, given the meet-list filter and the query.
 *
 * The prototype filters on name, role and interests. A roster card carries no interests
 * (`MEM-011` AC-6 fixes its six fields, and interests are one of the things it deliberately leaves
 * off), so the query runs over the four fields a card actually shows: first name, last name, role
 * and company. Searching a field a member cannot see would produce a result they cannot explain.
 *
 * **The conversation starter is deliberately not searched.** It is somebody's own sentence about
 * what they want to talk about, and making it queryable turns the roster into the directory
 * `MEM-011`'s Open questions refuse — *"a roster you can query is a directory"* is the roster
 * page's own note, and the reason that page has no search at all. The search here is the
 * prototype's, over a list the member is entitled to because they are registered, and it is held
 * to the fields that identify a person rather than the ones that describe them.
 */
export function attendeesMatching<
  T extends {
    readonly attendeeRef: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly role: string | null;
    readonly company: string | null;
  },
>(
  attendees: ReadonlyArray<T>,
  filter: {
    readonly query: string;
    readonly meetOnly: boolean;
    readonly savedRefs: ReadonlySet<string>;
  },
): ReadonlyArray<T> {
  const base = filter.meetOnly
    ? attendees.filter((attendee) => filter.savedRefs.has(attendee.attendeeRef))
    : attendees;
  const query = filter.query.trim().toLowerCase();
  if (query.length === 0) return base;
  return base.filter((attendee) =>
    [attendee.firstName, attendee.lastName, attendee.role, attendee.company]
      .filter((field): field is string => typeof field === 'string' && field.length > 0)
      .join(' ')
      .toLowerCase()
      .includes(query),
  );
}

/**
 * The prototype's `meetEmptyInline` (§2.4, item 4): the toggle is on and **nothing at all** is
 * saved, which is a different state from a query that matched none of a list that has entries.
 */
export const MEET_LIST_EMPTY_TITLE = 'Nobody saved yet';
export const MEET_LIST_EMPTY_DESCRIPTION =
  "Save people from your matches or the attendee list and they'll show up here.";
export const MEET_LIST_EMPTY_ACTION = 'Browse everyone attending';

/**
 * What a search that matched nobody says. The prototype has no such state — its six attendees are
 * fixtures and its search is never empty — and a grid that simply vanished under a typed word
 * reads as a bug rather than as an answer.
 */
export const ATTENDEES_NO_MATCH_TITLE = 'Nobody by that name';
export const ATTENDEES_NO_MATCH_DESCRIPTION =
  'Try part of a name, a role, or a company — or clear the search to see everyone.';

/** The empty roster, which is a real answer before anybody else has registered. */
export const ATTENDEES_EMPTY_TITLE = 'Nobody on the list yet';
export const ATTENDEES_EMPTY_DESCRIPTION =
  'As people register and choose to appear, they will show up here.';

/**
 * The **closed** roster, which is a different answer and used to borrow the one above — TW
 * 44159943, Brad's wording.
 *
 * Two moments shared one title. Before the preview window opens, the list is not empty: it exists
 * and is not being shown yet, and the sentence underneath already says when — *"Who's coming opens
 * Tuesday, September 22 at 6:00 PM"*. "Nobody on the list yet" contradicts that line, and it is
 * the state Brad was looking at when he asked for this wording.
 *
 * It is not used for the roster that **is** open and genuinely holds nobody, where the list is
 * available and the honest title is the one above. Asked on the task before building; the split is
 * what the triage comment proposed and nobody has asked for the two to read alike.
 */
export const ATTENDEES_CLOSED_TITLE = 'Attendee List Available Soon';
